// src/utils/analytics.js
// Centralized Google Analytics tracking for Invenire
// GA Measurement ID is configured in index.html: G-ZFXV9D3041

/**
 * Track a page view
 * @param {string} path - Page path (e.g., '/i/abc123')
 * @param {string} title - Page title
 */
export function trackPageView(path, title) {
  if (window.gtag) {
    window.gtag('event', 'page_view', {
      page_path: path,
      page_title: title,
      page_location: window.location.href
    });
    console.log('[Analytics] Page view:', path);
  }
}

/**
 * Track a custom event
 * @param {string} eventName - Event name (e.g., 'interview_started')
 * @param {object} params - Event parameters
 */
export function trackEvent(eventName, params = {}) {
  if (window.gtag) {
    window.gtag('event', eventName, params);
    console.log('[Analytics] Event:', eventName, params);
  }
}

// =============================================================================
// INTERVIEW EVENTS
// =============================================================================

export function trackInterviewLoaded(interviewId, companyName, jobTitle) {
  trackEvent('interview_loaded', {
    interview_id: interviewId,
    company_name: companyName,
    job_title: jobTitle
  });
}

export function trackInterviewError(interviewId, errorMessage) {
  trackEvent('interview_error', {
    interview_id: interviewId,
    error_message: errorMessage
  });
}

export function trackConsentGiven(interviewId) {
  trackEvent('consent_given', {
    interview_id: interviewId
  });
}

export function trackInterviewStarted(interviewId, sessionNumber = 1) {
  trackEvent('interview_started', {
    interview_id: interviewId,
    session_number: sessionNumber
  });
}

export function trackInterviewPaused(interviewId, reason, durationSeconds) {
  trackEvent('interview_paused', {
    interview_id: interviewId,
    pause_reason: reason,
    duration_seconds: durationSeconds
  });
}

export function trackInterviewCompleted(interviewId, durationSeconds, questionsAsked) {
  trackEvent('interview_completed', {
    interview_id: interviewId,
    duration_seconds: durationSeconds,
    questions_asked: questionsAsked
  });
}

export function trackRecordingUploaded(interviewId, fileSizeMB, status) {
  trackEvent('recording_uploaded', {
    interview_id: interviewId,
    file_size_mb: fileSizeMB,
    status: status // 'complete', 'error'
  });
}

// =============================================================================
// LANDING PAGE EVENTS
// =============================================================================

export function trackInviteSubmitted(tokenLength) {
  trackEvent('invite_submitted', {
    token_length: tokenLength
  });
}

// =============================================================================
// ADMIN EVENTS
// =============================================================================

export function trackJobCreated(jobId, clientId, source) {
  trackEvent('job_created', {
    job_id: jobId,
    client_id: clientId,
    source: source // 'url' or 'paste'
  });
}

export function trackCandidateInvited(interviewId, jobId, clientKey) {
  trackEvent('candidate_invited', {
    interview_id: interviewId,
    job_id: jobId,
    client_key: clientKey
  });
}

export function trackReportViewed(reportType, entityId) {
  trackEvent('report_viewed', {
    report_type: reportType, // 'job', 'candidate', 'summary'
    entity_id: entityId
  });
}

export function trackAnalysisTriggered(interviewId, isRetry) {
  trackEvent('analysis_triggered', {
    interview_id: interviewId,
    is_retry: isRetry
  });
}

export function trackReconnectSent(interviewId, method) {
  trackEvent('reconnect_sent', {
    interview_id: interviewId,
    method: method // 'email' or 'copy'
  });
}

export function trackClientUpdated(clientKey, isNew) {
  trackEvent('client_updated', {
    client_key: clientKey,
    is_new: isNew
  });
}

// =============================================================================
// 404 TRACKING
// =============================================================================

export function trackPageNotFound(attemptedPath) {
  trackEvent('page_not_found', {
    attempted_path: attemptedPath
  });
}
